// Lead Management Service Layer
// This service layer provides a clean interface for data operations
// Implements actual API calls to the backend

import axiosInstance from "../utils/axios";
import { transformLeadsArray } from "../utils/leadDataTransforms";
import { getStageAndSubStages } from "./leadStageService";

import { getCompanyIdFromToken, getUserIdFromToken } from "../utils/tokenUtils";
import { convertToUTC } from "../utils/dateUtils";
import {
  LEAD_STATUSES,
  LEAD_STAGES,
  LEAD_SOURCES,
  LEAD_PRIORITIES,
  LEAD_OWNERS,
  PROGRAMS,
  COURSES,
  INDIAN_STATES,
} from "../constants/data/leadsData.js";

// API Endpoints
const API_ENDPOINTS = {
  LEADS: "api/Leads",
  EMAIL: "api/Email",
  SMS: "api/Sms",
  BULK_ACTION: "api/Leads/BulkLeadActionDispatcher",
};

export const verifyOrRejectLeadDocument = async ({
  id,
  doneBy,
  verificationStatus,
  remarks,
} = {}) => {
  try {
    if (!id) {
      throw new Error("Document ID is required");
    }

    const payload = {
      id,
      doneby: doneBy || getUserIdFromToken(),
      verificationStatus,
      remarks,
    };

    if (!payload.doneby) {
      throw new Error("Unable to determine verifying user");
    }

    const response = await axiosInstance.post(
      `${API_ENDPOINTS.LEADS}/VerifyOrRejectLeadDocument`,
      payload,
    );

    const responseData = response.data;

    if (responseData.statusCode === 200 && responseData.status === "Success") {
      return {
        success: true,
        data: responseData.data,
        message: responseData.message || "Document verification updated successfully.",
      };
    }

    throw new Error(responseData.message || "Failed to update document verification status");
  } catch (error) {
    console.error("Error updating document verification status:", error);
    return {
      success: false,
      error: error.message || "Failed to update document verification status",
    };
  }
};

// Map frontend column names to backend field names for sorting
const SORT_COLUMN_MAPPING = {
  fullname: "fullname",
  stagename: "stagename",
  leadscore: "leadscore",
  createdat: "createdat",
  tags: "tags",
  // Keep old mappings for backward compatibility
  name: "FullName",
  status: "StageName",
  priority: "LeadScore",
  registrationDate: "CreatedAt",
};

// Alternative helper function for company ID extraction (used in bulkUpdateLeadStage)
const getCompanyId = () => {
  try {
    const fromEnv = import.meta?.env?.VITE_COMPANY_ID;
    if (fromEnv) return String(fromEnv);
    const token = localStorage.getItem("authToken");
    if (token) {
      const payloadBase64 = token.split(".")[1];
      const payload = JSON.parse(atob(payloadBase64));
      if (payload?.CompanyId) return String(payload.CompanyId);
      if (payload?.companyId) return String(payload.companyId);
    }
  } catch (_) {
    // noop
  }
  return undefined;
};
// ===== LEAD OPERATIONS =====

// Get all leads with optional filtering and pagination
export const getLeads = async (
  filters = {},
  pagination = { page: 1, limit: 25 },
  signal = null, // Add abort signal parameter
) => {
 
  try {
    // Get CompanyId from JWT token
    const companyId = getCompanyIdFromToken();
    const loggedInUserId = getUserIdFromToken();

    // Build request payload based on expected schema
    const payload = {
      search: filters.search || undefined,
      leadSourceId: Array.isArray(filters.leadSourceId)
        ? filters.leadSourceId
        : filters.leadSourceId && filters.leadSourceId !== "all"
          ? [filters.leadSourceId]
          : filters.source && filters.source !== "all"
            ? [filters.source]
            : undefined,
      leadSourceFormId: Array.isArray(filters.LeadSourceFormId)
        ? filters.LeadSourceFormId
        : filters.LeadSourceFormId && filters.LeadSourceFormId !== "all"
          ? [filters.LeadSourceFormId]
          : filters.formtype && filters.formtype !== "all"
            ? [filters.formtype]
            : undefined,
      priorityId: Array.isArray(filters.priority)
  ? filters.priority.join(",")
  : filters.priority || undefined,
 
interactionLevelId: Array.isArray(filters.interactionLevel)
  ? filters.interactionLevel.join(",")
  : filters.interactionLevel || undefined,
nationality: Array.isArray(filters.nationality)
        ? filters.nationality.map((val) => parseInt(val, 10))
        : filters.nationality
          ? [parseInt(filters.nationality, 10)]
          : undefined,
      leadStatus:
        filters.leadStatus && filters.leadStatus !== "all"
          ? parseInt(filters.leadStatus, 10)
          : filters.status && filters.status !== "all"
            ? parseInt(filters.status, 10)
            : undefined,
      // Build stageFilters from the new format (preferred) or fall back to old flat fields
      stageFilters: (() => {
        // New format: stageFilters array passed directly
        if (filters.stageFilters && Array.isArray(filters.stageFilters) && filters.stageFilters.length > 0) {
          return filters.stageFilters;
        }
        // Backward compat: flat stageId / subStageId fields
        const stageIds = Array.isArray(filters.stageId)
          ? filters.stageId
          : filters.stageId
            ? [filters.stageId]
            : [];
        const subStageIds = Array.isArray(filters.subStageId)
          ? filters.subStageId
          : filters.subStageId
            ? [filters.subStageId]
            : [];
        if (stageIds.length === 0 && subStageIds.length === 0) return undefined;
        // Map each stage; attach sub-stage IDs that belong to it
        return stageIds.map(stageId => ({
          stageId,
          subStageIds: subStageIds.length > 0 ? subStageIds : null,
        }));
      })(),
      scoreRange: (() => {
        const val = filters.leadScore || filters.scoreRange;
        if (!val) return undefined;
        // Handle array format and convert "-" to "|" for SP compatibility
        const actualVal = Array.isArray(val) ? val[0] : val;
        return typeof actualVal === "string" ? actualVal.replace("-", "|") : undefined;
      })(),
      assignedTo: Array.isArray(filters.assignedTo)
        ? filters.assignedTo
        : filters.assignedTo
          ? [filters.assignedTo]
          : undefined,

      teamId: filters.teamId || undefined,
      state: filters.state || undefined,
      fromDate: filters.fromDate
        ? convertToUTC(filters.fromDate, "00:00")
        : filters.dateRange?.fromDate
          ? convertToUTC(filters.dateRange.fromDate, "00:00")
          : undefined,
      toDate: filters.toDate
        ? convertToUTC(filters.toDate, "23:59")
        : filters.dateRange?.toDate
          ? convertToUTC(filters.dateRange.toDate, "23:59")
          : undefined,
      // Resolve applicantTab ("all" | "synced" | "notsynced") → isApplication + syncCrmToErp
      // All:        isApplication=true, syncCrmToErp=null  (all applicants regardless of sync)
      // Synced:     isApplication=true, syncCrmToErp=true  (only synced to ERP)
      // Not Synced: isApplication=true, syncCrmToErp=false (only not yet synced to ERP)
      // No tab set: fall back to legacy IsApplication flag only
      ...((() => {
        const tab = filters?.applicantTab;
        if (tab === "all")       return { isApplication: true,  syncCrmToErp: undefined };
        if (tab === "synced")    return { isApplication: true,  syncCrmToErp: true  };
        if (tab === "notsynced") return { isApplication: true,  syncCrmToErp: false };
        // Legacy / lead manager path
        return { isApplication: filters?.IsApplication };
      })()),
      loggedInUserId: loggedInUserId || undefined,
      viewMode: filters.viewMode !== undefined ? filters.viewMode : undefined,
      pageNumber: pagination.page > 0 ? pagination.page : 1, // Use 1-based indexing to match backend expectations
      pageSize: pagination.limit || 25,
      companyId: companyId || undefined,
      sortColumn: filters.sortBy ? (SORT_COLUMN_MAPPING[filters.sortBy] || filters.sortBy) : undefined,
      sortDirection: filters.sortOrder || undefined,
      leadview: filters.leadview !== undefined ? filters.leadview : 0,

      // Tag filter — structured Include/Exclude list
      // Each entry: { tagName: string, type: "include" | "exclude" }
      tags: Array.isArray(filters.tagNames) && filters.tagNames.length > 0
        ? filters.tagNames
        : undefined,

      // UTM Source filter — array of selected UTM source strings
      utmSources: Array.isArray(filters.utmSource) && filters.utmSource.length > 0
        ? filters.utmSource          // send as ["Facebook","Google"] — matches List<string> on backend
        : undefined,
    };

    // Handle grouped filters (new advanced filter format)
    // Only add groupedFilters if it has valid groups with rules
    if (
      filters.groupedFilters &&
      filters.groupedFilters.Groups &&
      filters.groupedFilters.Groups.length > 0 &&
      filters.groupedFilters.Groups[0].Rules &&
      filters.groupedFilters.Groups[0].Rules.length > 0
    ) {
      payload.groupedFilters = filters.groupedFilters;
    }

    // Remove undefined keys
    Object.keys(payload).forEach((key) => {
      if (payload[key] === undefined) {
        delete payload[key];
      }
    });

    // Make API call using POST instead of GET, with abort signal
    const response = await axiosInstance.post(API_ENDPOINTS.LEADS, payload, {
      signal, // Pass the abort signal to axios
    });
    const responseData = response.data;

    // Parse the response based on the API structure
    if (responseData.statusCode === 200 && responseData.status === "Success") {
      let leadsData = [];
      if (typeof responseData.data === "string") {
        try {
          leadsData = JSON.parse(responseData.data);
        } catch (parseError) {
          console.error("Error parsing leads data:", parseError);
          leadsData = [];
        }
      } else if (Array.isArray(responseData.data)) {
        leadsData = responseData.data;
      }

      const transformedLeads = transformLeadsArray(leadsData);

      return {
        success: true,
        data: transformedLeads,
        pagination: {
          page: pagination.page,
          limit: pagination.limit,
          total: responseData.totalRecords || transformedLeads.length,
          totalPages: Math.ceil(
            (responseData.totalRecords || transformedLeads.length) /
            pagination.limit,
          ),
          hasNext:
            pagination.page * pagination.limit <
            (responseData.totalRecords || transformedLeads.length),
          hasPrev: pagination.page > 1,
        },
        filters,
        message: responseData.message,
      };
    } else {
      throw new Error(responseData.message || "Failed to fetch leads");
    }
  } catch (error) {
    console.error("Error fetching leads:", error);

    return {
      success: false,
      error: error.message || "Failed to fetch leads",
      data: [],
      pagination: {
        page: pagination.page,
        limit: pagination.limit,
        total: 0,
        totalPages: 0,
        hasNext: false,
        hasPrev: false,
      },
      filters,
    };
  }
};

// Get a single lead by ID with structured response
export const getLead = async (leadId) => {
  try {
    const response = await axiosInstance.get(
      `${API_ENDPOINTS.LEADS}/${leadId}`,
    );
    const responseData = response.data;

    if (responseData.statusCode === 200 && responseData.status === "Success") {
      // Parse the data if it's a JSON string
      let leadData = responseData.data;
      if (typeof responseData.data === "string") {
        try {
          leadData = JSON.parse(responseData.data);
        } catch (parseError) {
          console.error("Error parsing lead data:", parseError);
          leadData = responseData.data;
        }
      }

      // Transform API response to match the exact required structure
      const structuredResponse = {
        message: "Lead details fetched successfully",
        statusCode: 200,
        status: "Success",
        data: leadData,
        totalRecords: 1,
      };

      return {
        success: true,
        data: structuredResponse,
      };
    } else {
      throw new Error(responseData.message || "Lead not found");
    }
  } catch (error) {
    console.error("Error fetching lead:", error);
    return {
      success: false,
      error: error.message || "Failed to fetch lead",
      data: {
        message: error.message || "Failed to fetch lead details",
        statusCode: 404,
        status: "Error",
        data: null,
        totalRecords: 0,
      },
    };
  }
};

// Create a new lead
export const createLead = async (leadData) => {
  try {
    const response = await axiosInstance.post(API_ENDPOINTS.LEADS, leadData);
    const responseData = response.data;

    if (responseData.statusCode === 200 && responseData.status === "Success") {
      return {
        success: true,
        data: responseData.data,
        message: responseData.message || "Lead created successfully",
      };
    } else {
      throw new Error(responseData.message || "Failed to create lead");
    }
  } catch (error) {
    console.error("Error creating lead:", error);
    return {
      success: false,
      error: error.message || "Failed to create lead",
      data: null,
    };
  }
};

// Update a lead
export const updateLead = async (leadId, updateData) => {
  try {
    const response = await axiosInstance.patch(
      `${API_ENDPOINTS.LEADS}/${leadId}`,
      updateData,
    );
    const responseData = response.data;

    if (responseData.statusCode === 200 && responseData.status === "Success") {
      return {
        success: true,
        data: responseData.data,
        message: responseData.message || "Lead updated successfully",
      };
    } else {
      throw new Error(responseData.message || "Failed to update lead");
    }
  } catch (error) {
    console.error("Error updating lead:", error);
    return {
      success: false,
      error: error.message || "Failed to update lead",
      data: null,
    };
  }
};

export const assignDocumentsToUsersForVerification = async ({
  leadId,
  userIds,
} = {}) => {
  try {
    if (!leadId) {
      throw new Error("Lead ID is required to assign documents for verification");
    }

    const userIdsArray = Array.isArray(userIds) ? userIds.filter(Boolean) : [];

    if (userIdsArray.length === 0) {
      throw new Error("At least one user ID is required to assign documents");
    }

    const payload = {
      leadId,
      userIds: userIdsArray,
      assignedBy: getUserIdFromToken(),
    };

    if (!payload.assignedBy) {
      throw new Error("Unable to determine assigning user");
    }

    const response = await axiosInstance.post(
      `${API_ENDPOINTS.LEADS}/AssignDocumentToUsersForVerification`,
      payload,
    );

    const responseData = response.data;

    if (responseData.statusCode === 200 && responseData.status === "Success") {
      let assignmentData = responseData.data;

      if (typeof assignmentData === "string") {
        try {
          assignmentData = JSON.parse(assignmentData);
        } catch (parseError) {
          console.error("Error parsing assignment data:", parseError);
        }
      }

      const recordsCount = Array.isArray(assignmentData)
        ? assignmentData.length
        : responseData.totalRecords || 0;

      return {
        success: true,
        data: assignmentData,
        message:
          responseData.message || "Lead documents assigned successfully.",
        totalRecords: responseData.totalRecords || recordsCount,
      };
    }

    throw new Error(
      responseData.message || "Failed to assign documents for verification",
    );
  } catch (error) {
    console.error("Error assigning documents for verification:", error);

    return {
      success: false,
      error: error.message || "Failed to assign documents for verification",
      data: [],
      totalRecords: 0,
    };
  }
};

// Final approve lead document
export const finalApproveLeadDoc = async (leadId) => {
  try {
    if (!leadId) {
      throw new Error("Lead ID is required");
    }

    const response = await axiosInstance.get(
      `${API_ENDPOINTS.LEADS}/FinalApproveLeadDoc/${leadId}`
    );

    const responseData = response.data;

    // Handle both success and error responses from the API
    if (responseData.statusCode === 200 && responseData.status === "Success") {
      return {
        success: true,
        data: responseData.data,
        message: responseData.message || "Lead documents final approved successfully",
        statusCode: responseData.statusCode,
        status: responseData.status,
        totalRecords: responseData.totalRecords || 0,
      };
    } else {
      // Return the error response as received from the API
      return {
        success: false,
        message: responseData.message || "Failed to final approve lead documents",
        statusCode: responseData.statusCode || 400,
        status: responseData.status || "Failed",
        data: responseData.data || null,
        totalRecords: responseData.totalRecords || 0,
      };
    }
  } catch (error) {
    console.error("Error final approving lead documents:", error);
    return {
      success: false,
      message: error.message || "Failed to final approve lead documents",
      statusCode: 500,
      status: "Error",
      data: null,
      totalRecords: 0,
    };
  }
};

// Assign lead document inspectors - new API
export const assignLeadDocInspectors = async ({
  leadId,
  teamIds = [],
  counsellorIds = [],
} = {}) => {
  try {
    if (!leadId) {
      throw new Error("Lead ID is required");
    }

    if (teamIds.length === 0 && counsellorIds.length === 0) {
      throw new Error("At least one team ID or counselor ID is required");
    }

    const userId = getUserIdFromToken();
    const companyId = getCompanyIdFromToken();

    if (!userId) {
      throw new Error("Unable to determine user ID");
    }

    if (!companyId) {
      throw new Error("Unable to determine company ID");
    }

    const payload = {
      leadId,
      teamIds: teamIds.join(","), // Convert array to comma-separated string
      counsellorIds: counsellorIds.join(","), // Convert array to comma-separated string
      userId,
      companyId,
    };

    const response = await axiosInstance.post(
      `${API_ENDPOINTS.LEADS}/AssignLeadDocInspectors`,
      payload,
    );

    const responseData = response.data;

    if (responseData.statusCode === 200 && responseData.status === "Success") {
      return {
        success: true,
        data: responseData.data,
        message: responseData.message || "Lead document inspectors assigned successfully",
      };
    }

    throw new Error(responseData.message || "Failed to assign lead document inspectors");
  } catch (error) {
    console.error("Error assigning lead document inspectors:", error);
    return {
      success: false,
      error: error.message || "Failed to assign lead document inspectors",
      data: null,
    };
  }
};

// Get teams/counselors assigned to document verification
export const getTeamAssignedToDocVerification = async (leadId) => {
  try {
    if (!leadId) {
      throw new Error("Lead ID is required");
    }

    const response = await axiosInstance.get(
      `${API_ENDPOINTS.LEADS}/GetTeamAssignedToDocVerification/${leadId}`
    );

    const responseData = response.data;

    if (responseData.statusCode === 200 && responseData.status === "Success") {
      let assignedData = [];

      // Parse the data field if it's a JSON string
      if (typeof responseData.data === "string") {
        try {
          assignedData = JSON.parse(responseData.data);
        } catch (parseError) {
          console.error("Error parsing assigned data:", parseError);
          assignedData = [];
        }
      } else if (Array.isArray(responseData.data)) {
        assignedData = responseData.data;
      }

      return {
        success: true,
        data: assignedData,
        message: responseData.message || "Assigned inspectors fetched successfully",
      };
    }

    throw new Error(responseData.message || "Failed to fetch assigned inspectors");
  } catch (error) {
    console.error("Error fetching assigned inspectors:", error);
    return {
      success: false,
      error: error.message || "Failed to fetch assigned inspectors",
      data: [],
    };
  }
};

// Update lead owner
export const updateLeadOwner = async (leadId, assignedTo) => {
  try {
    const payload = {
      leadId: typeof leadId === "string" ? [leadId] : leadId,
      assignedTo,
    };

    const response = await axiosInstance.post(
      `${API_ENDPOINTS.LEADS}/UpdateleadOwner`,
      payload,
    );
    const responseData = response.data;

    if (responseData.statusCode === 200 && responseData.status === "Success") {
      return {
        success: true,
        data: responseData.data,
        message: responseData.message || "Lead owner updated successfully",
      };
    } else {
      throw new Error(responseData.message || "Failed to update lead owner");
    }
  } catch (error) {
    console.error("Error updating lead owner:", error);
    return {
      success: false,
      error: error.message || "Failed to update lead owner",
      data: null,
    };
  }
};

/**
 * Fetch accountant counsellors list
 * @returns {Promise} Response with accountant counsellors data
 */
export const getAccountantCounsellors = async () => {
  try {
    const response = await axiosInstance.get(`${API_ENDPOINTS.LEADS}/GetAccountantCounsellors`);
    const responseData = response.data;

    if (responseData.statusCode === 200 && responseData.status === "Success") {
      let counselors = [];
      if (typeof responseData.data === "string") {
        try {
          counselors = JSON.parse(responseData.data);
        } catch (parseError) {
          console.error("Error parsing accountant counselors data:", parseError);
          counselors = [];
        }
      } else if (Array.isArray(responseData.data)) {
        counselors = responseData.data;
      }
      return {
        success: true,
        data: counselors,
        message: responseData.message || "Accountant counselors fetched successfully",
      };
    }
    throw new Error(responseData.message || "Failed to fetch accountant counselors");
  } catch (error) {
    console.error("Error fetching accountant counselors:", error);
    return {
      success: false,
      error: error.message || "Failed to fetch accountant counselors",
      data: [],
    };
  }
};

/**
 * Assign accountant counsellor to a lead
 * @param {Object} payload { leadId, accountantCounsellorId, companyId, modifiedBy }
 * @returns {Promise} Response with assignment result
 */
export const assignAccountantCounsellor = async (payload) => {
  try {
    const response = await axiosInstance.post(`${API_ENDPOINTS.LEADS}/AssignAccountantCounsellor`, payload);
    const responseData = response.data;

    if (responseData.statusCode === 200 && responseData.status === "Success") {
      return {
        success: true,
        data: responseData.data,
        message: responseData.message || "Accountant counsellor assigned successfully",
      };
    }
    throw new Error(responseData.message || "Failed to assign accountant counsellor");
  } catch (error) {
    console.error("Error assigning accountant counsellor:", error);
    return {
      success: false,
      error: error.message || "Failed to assign accountant counsellor",
      data: null,
    };
  }
};

/**
 * Get list of academic managers/counsellors available for assignment
 * @returns {Promise} List of academic managers
 */
export const getAcademicCounsellors = async () => {
  try {
    const response = await axiosInstance.get(`${API_ENDPOINTS.LEADS}/GetAcademicCounsellors`);
    const responseData = response.data;

    if (responseData.statusCode === 200 && responseData.status === "Success") {
      let counselors = [];
      if (typeof responseData.data === "string") {
        try {
          counselors = JSON.parse(responseData.data);
        } catch (parseError) {
          console.error("Error parsing academic counselors data:", parseError);
          counselors = [];
        }
      } else if (Array.isArray(responseData.data)) {
        counselors = responseData.data;
      }
      return {
        success: true,
        data: counselors,
        message: responseData.message || "Academic managers fetched successfully",
      };
    }
    throw new Error(responseData.message || "Failed to fetch academic managers");
  } catch (error) {
    console.error("Error fetching academic managers:", error);
    return {
      success: false,
      error: error.message || "Failed to fetch academic managers",
      data: [],
    };
  }
};

/**
 * Assign academic counsellor/manager to a lead
 * @param {Object} payload { leadId, academicCounsellorId, companyId, modifiedBy }
 * @returns {Promise} Response with assignment result
 */
export const assignAcademicCounsellor = async (payload) => {
  try {
    const response = await axiosInstance.post(
      `${API_ENDPOINTS.LEADS}/UpdateAcademicCounsellor`,
      payload
    );
    const responseData = response.data;

    if (responseData.statusCode === 200 && responseData.status === "Success") {
      return {
        success: true,
        data: responseData.data,
        message: responseData.message || "Academic manager assigned successfully",
      };
    }
    throw new Error(responseData.message || "Failed to assign academic manager");
  } catch (error) {
    console.error("Error assigning academic manager:", error);
    return {
      success: false,
      error: error.message || "Failed to assign academic manager",
      data: null,
    };
  }
};

export const generateOfferLetterForInternationalStudent = async ({
  leadId,
  rawJson,
  offerLetterFields = [],
  intLeadsOfferLetterId = null,
  generatedHtml = null,
} = {}) => {
  try {
    if (!leadId) {
      throw new Error('leadId is required');
    }
    // Ensure payload matches backend expectations
    const payload = {
      leadId,
      rawJson: rawJson || '',
      offerLetterFields,
      generatedHtml: generatedHtml || null,
    };

    // Include the record ID when editing an existing offer letter
    if (intLeadsOfferLetterId) {
      payload.intLeadsOfferLetterId = intLeadsOfferLetterId;
    }

    const response = await axiosInstance.post(
      `${API_ENDPOINTS.LEADS}/GenerateOfferLetterForInternationalStudent`,
      payload,
    );
    const responseData = response.data;

    if (responseData.statusCode === 200 && responseData.status === "Success") {
      // Parse data if it's a JSON string
      let data = responseData.data;
      if (typeof data === "string") {
        try {
          data = JSON.parse(data);
        } catch (e) {
          console.error("Error parsing GenerateOfferLetterForInternationalStudent data:", e);
        }
      }
      return {
        success: true,
        data,
        message: responseData.message || "Offer letter generated successfully",
      };
    }
    throw new Error(responseData.message || "Failed to generate offer letter");
  } catch (error) {
    console.error("Error generating international offer letter:", error);
    return {
      success: false,
      error: error.message || "Failed to generate offer letter",
      data: null,
    };
  }
};

/**
 * Fetch an existing saved international offer letter by Lead ID
 * @param {string} leadId - Lead GUID
 * @returns {Promise} Response with the saved offer letter record
 */
export const getInternationalOfferLetterByLeadId = async (leadId) => {
  try {
    if (!leadId) throw new Error('leadId is required');
    const response = await axiosInstance.post(
      `${API_ENDPOINTS.LEADS}/GetInternationalOfferLetterByLeadId?LeadId=${leadId}`
    );
    const responseData = response.data;
    if (responseData.statusCode === 200 && responseData.status === "Success") {
      let data = responseData.data;
      if (typeof data === "string") {
        try { data = JSON.parse(data); } catch (e) { /* keep as-is */ }
      }
      return { success: true, data, message: responseData.message };
    }
    throw new Error(responseData.message || "Failed to fetch offer letter");
  } catch (error) {
    console.error("Error fetching international offer letter:", error);
    return { success: false, error: error.message, data: null };
  }
};


/**
 * Auto Assign academic counsellor using backend round-robin logic
 * @param {string} leadId - Lead ID
 * @returns {Promise} Response with assignment result
 */
export const autoAssignAcademicCounsellor = async (leadId) => {
  try {
    const response = await axiosInstance.post(
      `${API_ENDPOINTS.LEADS}/AutoAssignAcademicCounsellor`,
      `"${leadId}"`,
      { headers: { 'Content-Type': 'application/json' } }
    );
    const responseData = response.data;

    if (responseData.statusCode === 200 && responseData.status === "Success") {
      return {
        success: true,
        data: responseData.data,
        message: responseData.message || "Academic manager auto-assigned successfully",
      };
    }
    throw new Error(responseData.message || "Failed to auto-assign academic manager");
  } catch (error) {
    console.error("Error auto-assigning academic manager:", error);
    return {
      success: false,
      error: error.message || "Failed to auto-assign academic manager",
      data: null,
    };
  }
};

// Delete a lead
export const deleteLead = async (leadIds) => {
  try {
    // Ensure leadIds is always an array
    const leadIdsArray = Array.isArray(leadIds) ? leadIds : [leadIds];

    // Get CompanyId from JWT token
    const companyId = getCompanyIdFromToken();

    // Prepare payload
    const payload = {
      leadIds: leadIdsArray,
      companyId,
    };

    // Send DELETE request with data in config
    const response = await axiosInstance.delete(`${API_ENDPOINTS.LEADS}/bulk`, {
      data: payload,
    });

    const responseData = response.data;

    if (responseData.statusCode === 200 && responseData.status === "Success") {
      // Parse the data field if it's a string
      let deletedData = responseData.data;
      if (typeof deletedData === "string") {
        try {
          deletedData = JSON.parse(deletedData);
        } catch (parseError) {
          console.error("Error parsing deleted data:", parseError);
          deletedData = {};
        }
      }

      return {
        success: true,
        message: responseData.message || "Lead(s) deleted successfully",
        deletedCount: deletedData?.deletedCount || leadIdsArray.length,
      };
    } else {
      throw new Error(responseData.message || "Failed to delete lead(s)");
    }
  } catch (error) {
    console.error("Error deleting lead(s):", error);
    return {
      success: false,
      error: error.message || "Failed to delete lead(s)",
    };
  }
};

// Get advanced filter fields with operators and dropdown options
export const getAdvancedFilterFieldsWithOperators = async () => {
  try {
    const response = await axiosInstance.get("/api/Leads/advanced-filter-fields");

    if (response.data && response.data.success) {
      return {
        success: true,
        data: {
          dropdowns: response.data.data.dropdowns || {},
          operators: response.data.data.operators || {},
        },
        message: response.data.message || "Filter fields fetched successfully",
      };
    } else {
      throw new Error(response.data?.message || "Failed to fetch filter fields");
    }
  } catch (error) {
    console.error("Error fetching advanced filter fields:", error);
    return {
      success: false,
      error: error.message || "Failed to fetch advanced filter fields",
      data: { dropdowns: {}, operators: {} },
    };
  }
};

// Get master fields from FormDesk API
export const getMasterFields = async () => {
  try {
    const response = await axiosInstance.get("/api/FormDesk/GetMasterFields");
    const responseData = response.data;

    if (responseData.statusCode === 200 && responseData.status === "Success") {
      // Parse the data if it's a JSON string
      let categoriesData = [];
      if (typeof responseData.data === "string") {
        try {
          categoriesData = JSON.parse(responseData.data);
        } catch (parseError) {
          console.error("Error parsing master fields data:", parseError);
          categoriesData = [];
        }
      } else if (Array.isArray(responseData.data)) {
        categoriesData = responseData.data;
      }

      return {
        success: true,
        data: categoriesData,
        totalRecords: responseData.totalRecords,
        message: responseData.message || "Master fields fetched successfully",
      };
    } else {
      throw new Error(responseData.message || "Failed to fetch master fields");
    }
  } catch (error) {
    console.error("Error fetching master fields:", error);
    return {
      success: false,
      error: error.message || "Failed to fetch master fields",
      data: [],
      totalRecords: 0,
    };
  }
};

// Get advanced filter fields with their configuration
export const getAdvancedFilterFields = async () => {
  try {
    // Fetch the new API with dropdowns and operators
    const response = await axiosInstance.get("/api/Leads/advanced-filter-fields");

    if (!response.data || !response.data.success) {
      throw new Error(response.data?.message || "Failed to fetch filter fields");
    }

    const { dropdowns, operators } = response.data.data;
    const availableFields = [];

    // Map specific quick filter fields from the API dropdowns
    const quickFilterMappings = {
      "LeadSourceId": { key: "leadSourceId", label: "Lead Source", inputType: "select" },
      "Status": { key: "leadStatus", label: "Lead Status", inputType: "select" },
      "StageId": { key: "stageId", label: "Stage", inputType: "select" },
      "SubStageId": { key: "subStageId", label: "Sub Stage", inputType: "select" },
      "LeadScore": { key: "leadScore", label: "Lead Score", inputType: "select" },
      "AssignedTo": { key: "assignedTo", label: "Assigned To", inputType: "select" },
      "State": { key: "state", label: "State", inputType: "select" },
      "LeadSourceFormId": { key: "leadFormSourceId", label: "Formtype", inputType: "select" },
      "Nationality": { key: "nationality", label: "Nationality", inputType: "select" },
      "TagNames": { key: "tagNames", label: "Tags", inputType: "multi-select" },


    };

    // Process each dropdown from API
    Object.entries(dropdowns).forEach(([key, options]) => {
      const mapping = quickFilterMappings[key];
      if (mapping && Array.isArray(options)) {
        const fieldConfig = {
          id: key,
          fieldName: mapping.key,
          displayName: mapping.label,
          htmlInputType: mapping.inputType,
          inputType: mapping.inputType,
          hasDropdownOptions: true,
          dropdownOptions: options
            .filter(opt => opt.isActive !== false) // Only include active options
            .map((option) => ({
              id: option.value,
              label: option.label,
              value: option.value,
              description: option.description,
            })),
          options: options
            .filter(opt => opt.isActive !== false)
            .map((option) => ({
              id: option.value,
              label: option.label,
              value: option.value,
              description: option.description,
            })),
        };

        availableFields.push(fieldConfig);
      }
    });

    return {
      success: true,
      data: {
        availableFields,
        supportedOperators: Object.entries(operators).map(([key, value]) => ({
          label: key,
          value: value,
        })),
        dropdowns, // Raw dropdowns for quick filters
        operators, // Raw operators
      },
      message: "Filter fields fetched successfully",
    };
  } catch (error) {
    console.error("Error fetching advanced filter fields:", error);
    return {
      success: false,
      error: error.message || "Failed to fetch advanced filter fields",
      data: {
        availableFields: [],
        supportedOperators: [],
        dropdowns: {},
        operators: {},
      },
    };
  }
};

// Legacy function for backward compatibility - keeping old implementation
export const getAdvancedFilterFieldsLegacy = async () => {
  try {
    const masterFieldsResult = await getMasterFields();

    if (!masterFieldsResult.success) {
      throw new Error(masterFieldsResult.error || "Failed to fetch master fields");
    }

    const categories = masterFieldsResult.data || [];
    const availableFields = [];

    // Transform master fields into advanced filter fields format
    categories.forEach((category) => {
      if (category.MasterFields && Array.isArray(category.MasterFields)) {
        category.MasterFields.forEach((field) => {
          // Only include active fields
          if (field.IsActive) {
            const fieldConfig = {
              id: field.Id, // Master Field ID
              fieldName: field.FieldName,
              displayName: field.FieldLabel,
              categoryName: category.CategoryName,
              categoryId: category.Id,
              fieldTypeId: field.FieldTypeId,
              typeName: field.TypeName,
              htmlInputType: field.HtmlInputType,
              isCommon: field.IsCommon,
              isCompanyMasterField: field.IsCompanyMasterField === 1,
              placeholder: field.Placeholder,
              defaultValue: field.DefaultValue,
              options: [],
            };

            // Add options for dropdown, checkbox, radio fields
            if (field.MasterFormFieldOptions && Array.isArray(field.MasterFormFieldOptions) && field.MasterFormFieldOptions.length > 0) {
              fieldConfig.options = field.MasterFormFieldOptions
                .sort((a, b) => (a.OptionOrder || 0) - (b.OptionOrder || 0))
                .map((option) => ({
                  id: option.Id,
                  label: option.OptionLabel,
                  value: option.OptionValue,
                  order: option.OptionOrder,
                }));
              fieldConfig.hasDropdownOptions = true;
              fieldConfig.dropdownOptions = fieldConfig.options;
            }

            // Determine input type for UI
            switch (field.HtmlInputType) {
              case "text":
              case "textarea":
                fieldConfig.inputType = "text";
                break;
              case "number":
                fieldConfig.inputType = "number";
                break;
              case "email":
                fieldConfig.inputType = "email";
                break;
              case "date":
                fieldConfig.inputType = "date";
                break;
              case "select":
              case "checkbox":
              case "radio":
                fieldConfig.inputType = "select";
                break;
              default:
                fieldConfig.inputType = "text";
            }

            // Define allowed operators based on field type
            const operatorsByType = {
              text: ["=", "<>", "LIKE", "NOT LIKE", "IS NULL", "IS NOT NULL"],
              number: ["=", "<>", ">", "<", ">=", "<=", "IS NULL", "IS NOT NULL"],
              email: ["=", "<>", "LIKE", "NOT LIKE", "IS NULL", "IS NOT NULL"],
              date: ["=", "<>", ">", "<", ">=", "<=", "IS NULL", "IS NOT NULL"],
              select: ["=", "<>", "IN", "NOT IN", "IS NULL", "IS NOT NULL"],
            };

            fieldConfig.allowedOperators = operatorsByType[fieldConfig.inputType] || operatorsByType.text;

            availableFields.push(fieldConfig);
          }
        });
      }
    });

    return {
      success: true,
      data: {
        availableFields,
        supportedOperators: ["=", "<>", ">", "<", ">=", "<=", "LIKE", "NOT LIKE", "IN", "NOT IN", "IS NULL", "IS NOT NULL"],
        categories,
      },
    };
  } catch (error) {
    console.error("Error fetching advanced filter fields:", error);
    return {
      success: false,
      error: error.message || "Failed to fetch advanced filter fields",
      data: {
        availableFields: [],
        supportedOperators: ["=", "<>", ">", "<", ">=", "<=", "LIKE", "NOT LIKE", "IN", "NOT IN"],
        categories: [],
      },
    };
  }
};
// ===== LEADS IMPORT (EXCEL) =====

// POST /api/Leads/import/excel with multipart/form-data
// file: a File/Blob (from input[type="file"]) or equivalent
// optionalFields: object of extra fields if backend expects them (e.g., companyId)
// export const importLeadsFromExcel = async (file, optionalFields = {}) => {
//   try {
//     if (!file) {
//       return {
//         success: false,
//         error: "No file provided",
//         errors: ["Please select a valid Excel file to upload."]
//       };
//     }

//     // Client-side validation
//     const validExtensions = ['.xlsx', '.xls', '.csv'];
//     const fileName = file.name.toLowerCase();
//     const hasValidExtension = validExtensions.some(ext => fileName.endsWith(ext));

//     if (!hasValidExtension) {
//       return {
//         success: false,
//         error: "Invalid file format",
//         errors: [`File must be an Excel (.xlsx, .xls) or CSV (.csv) file. Received: ${file.name}`]
//       };
//     }

//     if (file.size === 0) {
//       return {
//         success: false,
//         error: "Empty file",
//         errors: ["The selected file is empty. Please choose a file with lead data."]
//       };
//     }

//     if (file.size > 10 * 1024 * 1024) { // 10MB limit
//       return {
//         success: false,
//         error: "File too large",
//         errors: [`File size (${(file.size / (1024 * 1024)).toFixed(2)}MB) exceeds the 10MB limit.`]
//       };
//     }

//     const formData = new FormData();
//     // Common backend expectations: field name like 'file' or 'excel'
//     formData.append("file", file); // adjust key if backend requires a different name [2][8]

//     // Append any extra fields (e.g., companyId) if needed by API
//     Object.entries(optionalFields).forEach(([k, v]) => {
//       if (v !== undefined && v !== null) formData.append(k, v);
//     }); // FormData appending pattern [6]



//     const response = await axiosInstance.post(
//       `${API_ENDPOINTS.LEADS}/import/leads`,
//       formData,
//       {
//         headers: {
//           // Let the browser set correct multipart boundary; do not hardcode it
//           "Content-Type": "multipart/form-data",
//         }, // multipart guidance [8][9]
//       },
//     );

//     // Axios parses JSON automatically when response is JSON
//     const data = response.data; // JSON handling [10]


//     // Enhanced validation of response
//     const isSuccess = !!data.success;
//     const totalRecords = parseInt(data.totalRecords) || 0;
//     const successfulImports = parseInt(data.successfulImports) || 0;
//     const failedImports = parseInt(data.failedImports) || 0;

//     // Check if the import was truly successful
//     if (isSuccess && totalRecords === 0 && successfulImports === 0) {
//       return {
//         success: false,
//         error: "No valid data found in file",
//         message: "The file was processed but no valid lead data was found.",
//         totalRecords: 0,
//         successfulImports: 0,
//         failedImports: 0,
//         errors: [
//           "The file may be empty or contain invalid data format.",
//           "Please ensure the file has proper headers and lead data.",
//           "Download the template to see the expected format."
//         ],
//         raw: data,
//       };
//     }

//     return {
//       success: isSuccess,
//       message: data.message || (isSuccess ? "Leads imported successfully" : "Import failed"),
//       totalRecords,
//       successfulImports,
//       failedImports,
//       errors: Array.isArray(data.errors) ? data.errors : [],
//       raw: data, // keep raw for debugging if needed
//     };
//   } catch (error) {
//     // Bubble up server-provided error details if available
//     const serverMsg = error?.response?.data?.message;
//     const serverErrors = error?.response?.data?.errors;
//     const statusCode = error?.response?.status;

//     console.error("Error importing leads (excel):", {
//       message: error.message,
//       status: statusCode,
//       serverMessage: serverMsg,
//       serverErrors,
//       response: error?.response?.data
//     });

//     // Provide more specific error messages based on status code
//     let errorMessage = serverMsg || error.message || "Failed to import leads";
//     let errors = Array.isArray(serverErrors) ? serverErrors : [];

//     if (statusCode === 400) {
//       errorMessage = "Invalid file or data format";
//       if (errors.length === 0) {
//         errors = ["The file format or content is not valid for lead import."];
//       }
//     } else if (statusCode === 413) {
//       errorMessage = "File too large";
//       errors = ["The uploaded file exceeds the server's size limit."];
//     } else if (statusCode === 415) {
//       errorMessage = "Unsupported file type";
//       errors = ["The file type is not supported. Please upload an Excel (.xlsx, .xls) or CSV file."];
//     } else if (statusCode >= 500) {
//       errorMessage = "Server error during import";
//       errors = ["There was a server error processing your file. Please try again later."];
//     }

//     return {
//       success: false,
//       error: errorMessage,
//       errors,
//       raw: error?.response?.data,
//     };
//   }
// };

export const importLeadsFromExcel = async (file, optionalFields = {}) => {
  try {
    if (!file) {
      return {
        success: false,
        error: "No file provided",
        errors: ["Please select a valid Excel file to upload."]
      };
    }

    // Client-side validation (keep existing code)
    const validExtensions = ['.xlsx', '.xls', '.csv'];
    const fileName = file.name.toLowerCase();
    const hasValidExtension = validExtensions.some(ext => fileName.endsWith(ext));

    if (!hasValidExtension) {
      return {
        success: false,
        error: "Invalid file format",
        errors: [`File must be an Excel (.xlsx, .xls) or CSV (.csv) file. Received: ${file.name}`]
      };
    }

    if (file.size === 0) {
      return {
        success: false,
        error: "Empty file",
        errors: ["The selected file is empty. Please choose a file with lead data."]
      };
    }

    if (file.size > 10 * 1024 * 1024) {
      return {
        success: false,
        error: "File too large",
        errors: [`File size (${(file.size / (1024 * 1024)).toFixed(2)}MB) exceeds the 10MB limit.`]
      };
    }

    const formData = new FormData();
    formData.append("file", file);

    Object.entries(optionalFields).forEach(([k, v]) => {
      if (v !== undefined && v !== null) formData.append(k, v);
    });

    const response = await axiosInstance.post(
      `${API_ENDPOINTS.LEADS}/import/leads`,
      formData,
      {
        // Content-Type handled by axios interceptor for FormData
        timeout: 120000, // 2 minute timeout for file imports
      },
    );

    const data = response.data;

    // **FIX: Map your API response structure to expected frontend structure**
    // Your API returns: status, statusCode, message, totalRecords, successfulImports, failedImports
    // Frontend expects: success, message, totalRecords, successfulImports, failedImports, errors

    const totalRecords = Number(data.totalRecords) || 0;
    const successfulImports = Number(data.successfulImports) || 0;
    const failedImports = Number(data.failedImports) || 0;

    const isSuccess =
      data.status === "Success" ||
      data.statusCode === 200 ||
      successfulImports > 0;

    /* ---------------------------------------------
     * 5. Handle failure or partial failure
     * --------------------------------------------- */
    if (!isSuccess || successfulImports === 0) {
      return {
        success: false,
        message: data.message || "Failed to import leads",
        totalRecords,
        successfulImports,
        failedImports,
        status: data.status,
        errorLeadsJson: data.errorLeadsJson,
        headers: data.headers,
      };
    }

    /* ---------------------------------------------
     * 6. Handle success
     * --------------------------------------------- */
    return {
      success: true,
      message: data.message || "Leads imported successfully",
      totalRecords,
      successfulImports,
      failedImports,
    };
  } catch (error) {
    /* ---------------------------------------------
     * 7. Handle API errors (400 / 500 etc.)
     * --------------------------------------------- */
    const data = error?.response?.data;

    return {
      success: false,
      message: data?.message || "Failed to import leads",
      error: data?.message || "Failed to import leads",
      errors: data?.message ? [data.message] : [],
      totalRecords: Number(data?.totalRecords) || 0,
      successfulImports: Number(data?.successfulImports) || 0,
      failedImports: Number(data?.failedImports) || 0,
      status: data?.status,
      errorLeadsJson: data?.errorLeadsJson,
      headers: data?.headers,
    };
  }
};


// ===== DOCUMENT DOWNLOAD =====

// GET /api/Leads/DownloadLeadDocument and trigger a direct download of the document
export const downloadLeadDocument = async (
  documentId,
  defaultFileName = "document",
) => {
  try {
    const url = `${API_ENDPOINTS.LEADS}/DownloadLeadDocument?documentId=${documentId}`; // /api/Leads/DownloadLeadDocument
    const response = await axiosInstance.get(url, {
      responseType: "blob", // ensure binary stream for documents
    });
    // Determine filename from Content-Disposition header if provided
    const contentDisposition = response.headers?.["content-disposition"];
    let fileName = defaultFileName;
    if (contentDisposition) {
      const match = contentDisposition.match(/filename="?([^"]+)"?/);
      if (match && match[1]) fileName = match[1];
    }

    // Create blob and programmatically download
    const blob = new Blob([response.data]); // build Blob from binary response
    const href = window.URL.createObjectURL(blob); // object URL for blob
    const a = document.createElement("a");
    a.href = href;
    a.download = fileName;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(href); // cleanup

    return { success: true, message: "Document download started" };
  } catch (error) {
    return {
      success: false,
      error: error.message || "Failed to download lead document",
    };
  }
};

// ===== IMPORT TEMPLATE DOWNLOAD =====

// GET /api/Leads/import/template and trigger a direct download of the Excel file
export const downloadLeadsImportTemplate = async (
  defaultFileName = "LeadsImportTemplate.xlsx",
) => {
  try {
    const url = `${API_ENDPOINTS.LEADS}/download/template`; // /api/Leads/import/template [1]
    const response = await axiosInstance.get(url, {
      responseType: "blob", // ensure binary stream for Excel [1][6]
    });

    // Determine filename from Content-Disposition header if provided
    const contentDisposition = response.headers?.["content-disposition"]; // header parsing [6]
    let fileName = defaultFileName;
    if (contentDisposition) {
      const match = contentDisposition.match(/filename="?([^"]+)"?/);
      if (match && match[1]) fileName = match[1];
    }

    // Create blob and programmatically download
    const blob = new Blob([response.data]); // build Blob from binary response [1][11]
    const href = window.URL.createObjectURL(blob); // object URL for blob [11]
    const a = document.createElement("a");
    a.href = href;
    a.download = fileName;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(href); // cleanup [6][11]

    return { success: true, message: "Template download started" };
  } catch (error) {
    console.error("Error downloading import template:", error);
    return {
      success: false,
      error: error.message || "Failed to download import template",
    };
  }
};


export const downloadLeadsErrorExcel = async (
  payload,
  defaultFileName = "Leads_Error_Report.xlsx"
) => {
  try {
    const url = `${API_ENDPOINTS.LEADS}/download-error-excel`;

    const response = await axiosInstance.post(url, payload, {
      responseType: "blob", // important for Excel download
      headers: {
        "Content-Type": "application/json",
      },
    });

    // Extract filename from Content-Disposition header (if backend sends it)
    const contentDisposition = response.headers?.["content-disposition"];
    let fileName = defaultFileName;

    if (contentDisposition) {
      const match = contentDisposition.match(/filename="?([^"]+)"?/);
      if (match?.[1]) {
        fileName = match[1];
      }
    }

    // Create Blob & trigger download
    const blob = new Blob([response.data], {
      type:
        response.headers["content-type"] ||
        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });

    const href = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = href;
    a.download = fileName;

    document.body.appendChild(a);
    a.click();

    document.body.removeChild(a);
    window.URL.revokeObjectURL(href);

    return { success: true, message: "Error Excel download started" };
  } catch (error) {
    console.error("Error downloading error excel:", error);

    return {
      success: false,
      error:
        error?.response?.data?.message ||
        error.message ||
        "Failed to download error excel",
    };
  }
};

// ===== BULK OPERATIONS =====

// Bulk update leads
export const bulkUpdateLeads = async (leadIds, updateData) => {
  try {
    const response = await axiosInstance.patch(`${API_ENDPOINTS.LEADS}/bulk`, {
      leadIds,
      updateData,
    });
    const responseData = response.data;

    if (responseData.statusCode === 200 && responseData.status === "Success") {
      return {
        success: true,
        data: responseData.data,
        message: responseData.message || "Bulk update completed successfully",
      };
    } else {
      throw new Error(responseData.message || "Failed to bulk update leads");
    }
  } catch (error) {
    console.error("Error bulk updating leads:", error);
    return {
      success: false,
      error: error.message || "Failed to bulk update leads",
      data: null,
    };
  }
};

// ===== PRIORITY OPERATIONS =====

// Get all priority levels
export const getPriorityList = async () => {
  try {
    const response = await axiosInstance.get(
      `${API_ENDPOINTS.LEADS}/GetPriorityList`,
    );
    const responseData = response.data;

    if (responseData.statusCode === 200 && responseData.status === "Success") {
      // Parse the data if it's a JSON string
      let priorityData = [];
      if (typeof responseData.data === "string") {
        try {
          priorityData = JSON.parse(responseData.data);
        } catch (parseError) {
          console.error("Error parsing priority data:", parseError);
          priorityData = [];
        }
      } else if (Array.isArray(responseData.data)) {
        priorityData = responseData.data;
      }

      return {
        success: true,
        data: priorityData,
        message: responseData.message || "Priority list fetched successfully",
      };
    } else {
      throw new Error(responseData.message || "Failed to fetch priority list");
    }
  } catch (error) {
    console.error("Error fetching priority list:", error);
    return {
      success: false,
      error: error.message || "Failed to fetch priority list",
      data: [],
    };
  }
};

// Set lead priority
export const setLeadPriority = async (leadId, priorityId) => {
  try {
    const companyId = getCompanyIdFromToken();
    const updatedBy = getUserIdFromToken();

    if (!companyId || !updatedBy) {
      throw new Error("Company ID or User ID not found in token");
    }

    const payload = {
      leadId,
      priorityId,
      updatedBy,
      companyId,
    };

    const response = await axiosInstance.post(
      `${API_ENDPOINTS.LEADS}/SetLeadPriority`,
      payload,
    );
    const responseData = response.data;

    if (responseData.statusCode === 200 && responseData.status === "Success") {
      // Parse the data if it's a JSON string
      let leadData = responseData.data;
      if (typeof responseData.data === "string") {
        try {
          leadData = JSON.parse(responseData.data);
        } catch (parseError) {
          console.error("Error parsing lead data:", parseError);
          leadData = responseData.data;
        }
      }

      return {
        success: true,
        data: leadData,
        message: responseData.message || "Lead priority updated successfully",
      };
    } else {
      throw new Error(responseData.message || "Failed to set lead priority");
    }
  } catch (error) {
    console.error("Error setting lead priority:", error);
    return {
      success: false,
      error: error.message || "Failed to set lead priority",
      data: null,
    };
  }
};

// ===== LEAD JOURNEY INDICATORS =====

/**
 * Get lead journey indicators for progress tracking
 * @param {string} leadId - The ID of the lead
 * @returns {Promise} Response with journey indicators
 */
export const getLeadJourneyIndicators = async (leadId) => {
  try {
    if (!leadId) {
      return {
        success: false,
        message: 'Lead ID is required',
        data: null,
      };
    }

    const response = await axiosInstance.post(
      '/api/Leads/GetLeadJourneyIndicatorsByLeadId',
      {},
      {
        params: {
          leadId,
        },
        showLoader: false,
      }
    );

    const resData = response.data;

    // Success case
    if (resData?.statusCode === 200 && resData?.status === 'Success') {
      let journeyData = resData.data;

      // Parse the data if it's a JSON string
      if (typeof resData.data === 'string') {
        try {
          journeyData = JSON.parse(resData.data);
        } catch (parseError) {
          console.error('Error parsing journey indicators data:', parseError);
          journeyData = resData.data;
        }
      }

      return {
        success: true,
        message: resData.message || 'Journey indicators fetched successfully',
        data: journeyData,
      };
    }

    // Failure response from API
    return {
      success: false,
      message: resData?.message || 'Failed to fetch journey indicators',
      data: null,
    };
  } catch (error) {
    console.error('Error fetching journey indicators:', error);

    return {
      success: false,
      message: 'Something went wrong while fetching journey indicators',
      error: error.response?.data?.message || error.message,
      data: null,
    };
  }
};

// ===== INTERACTION OPERATIONS =====

// Get all interaction levels
export const getInteractionLevelList = async () => {
  try {
    const response = await axiosInstance.get(
      `${API_ENDPOINTS.LEADS}/GetInteractionLevelList`,
    );
    const responseData = response.data;

    if (responseData.statusCode === 200 && responseData.status === "Success") {
      // Parse the data if it's a JSON string
      let interactionData = [];
      if (typeof responseData.data === "string") {
        try {
          interactionData = JSON.parse(responseData.data);
        } catch (parseError) {
          console.error("Error parsing interaction data:", parseError);
          interactionData = [];
        }
      } else if (Array.isArray(responseData.data)) {
        interactionData = responseData.data;
      }

      return {
        success: true,
        data: interactionData,
        message:
          responseData.message || "Interaction level list fetched successfully",
      };
    } else {
      throw new Error(
        responseData.message || "Failed to fetch interaction level list",
      );
    }
  } catch (error) {
    console.error("Error fetching interaction level list:", error);
    return {
      success: false,
      error: error.message || "Failed to fetch interaction level list",
      data: [],
    };
  }
};

// Set lead interaction level
export const setLeadInteractionLevel = async (leadId, interactionLevelId) => {
  try {
    const companyId = getCompanyIdFromToken();
    const updatedBy = getUserIdFromToken();

    if (!companyId || !updatedBy) {
      throw new Error("Company ID or User ID not found in token");
    }

    const payload = {
      leadId,
      leadInteractionLevelId: interactionLevelId,
      updatedBy,
      companyId,
    };

    const response = await axiosInstance.post(
      `${API_ENDPOINTS.LEADS}/SetLeadInteractionLevel`,
      payload,
    );
    const responseData = response.data;

    if (responseData.statusCode === 200 && responseData.status === "Success") {
      // Parse the data if it's a JSON string
      let leadData = responseData.data;
      if (typeof responseData.data === "string") {
        try {
          leadData = JSON.parse(responseData.data);
        } catch (parseError) {
          console.error("Error parsing lead data:", parseError);
          leadData = responseData.data;
        }
      }

      return {
        success: true,
        data: leadData,
        message:
          responseData.message || "Lead interaction level updated successfully",
      };
    } else {
      throw new Error(
        responseData.message || "Failed to set lead interaction level",
      );
    }
  } catch (error) {
    console.error("Error setting lead interaction level:", error);
    return {
      success: false,
      error: error.message || "Failed to set lead interaction level",
      data: null,
    };
  }
};
// ===== BULK STAGE UPDATE =====

// Helper to get ISO timestamp
const getIsoNow = () => new Date().toISOString(); // e.g., 2025-09-02T06:48:02.622Z [6]

// Bulk update lead stage/substage for multiple leads
export const bulkUpdateLeadStage = async ({
  leadIds,
  stageId,
  subStageId,
  remarks,
  leadScore, // optional lead score to update
  modifiedAt, // optional override
}) => {
  try {
    // Derive company and user from JWT like other methods
    const companyId = getCompanyIdFromToken() || getCompanyId(); // matches existing pattern [7]
    const modifiedBy = getUserIdFromToken(); // uses nameid claim, aligns with JWT id best practices [7]

    // Build payload exactly as requested
    const payload = {
      leadIds, // array of GUIDs
      stageId, // GUID
      subStageId, // GUID
      companyId, // GUID from token
      modifiedBy, // GUID/user id from token
      modifiedAt: modifiedAt || getIsoNow(), // ISO-8601 timestamp [6]
      remarks: remarks || "",
    };

    // Add leadScore to payload if provided
    if (leadScore !== undefined && leadScore !== null) {
      payload.leadScore = leadScore;
    }

    // POST request to the new endpoint
    const response = await axiosInstance.post(
      `${API_ENDPOINTS.LEADS}/BulkUpdateLeadStage`,
      payload,
    ); // axios POST pattern [6][9]

    const responseData = response.data;

    if (responseData.statusCode === 200 && responseData.status === "Success") {
      return {
        success: true,
        data: responseData.data,
        message: responseData.message || "Lead stage(s) updated successfully",
      };
    } else {
      throw new Error(
        responseData.message || "Failed to bulk update lead stage",
      );
    }
  } catch (error) {
    console.error("Error bulk updating lead stage:", error);
    return {
      success: false,
      error: error.message || "Failed to bulk update lead stage",
      data: null,
    };
  }
};

// Bulk Lead Action Dispatcher - Handles bulk operations with filtering capabilities
export const bulkLeadActionDispatcher = async (actionData) => {
  try {
    const {
      // Filter parameters (same as getLeads)
      search,
      leadSourceId,
      leadStatus,
      stageId,
      subStageId,
      scoreRange,
      assignedTo,
      teamId,
      state,
      fromDate,
      toDate,
      isApplication,
      includeAllFiltered,
      leadIds,
      staticFields,
      loggedInUserId,
      viewMode,
      pageNumber,
      pageSize,
      advancedFilters,
      groupedFilters,
      companyId,
      sortColumn,
      sortDirection,
      leadview,

      // Action parameters
      actionType,
      updateStageId,
      updateSubStageId,
      updateAssignedTo,
      remarks,
    } = actionData;

    // Build the payload following the backend model structure (same as getLeads)
    const payload = {
      // Filter parameters (same as getLeads)
      search: actionData.search || undefined,
      leadSourceId: Array.isArray(actionData.leadSourceId)
        ? actionData.leadSourceId
        : actionData.leadSourceId && actionData.leadSourceId !== "all"
          ? [actionData.leadSourceId]
          : actionData.source && actionData.source !== "all"
            ? [actionData.source]
            : undefined,
      leadStatus: Array.isArray(actionData.leadStatus)
        ? actionData.leadStatus
        : actionData.leadStatus && actionData.leadStatus !== "all"
          ? [parseInt(actionData.leadStatus, 10)]
          : actionData.status && actionData.status !== "all"
            ? [parseInt(actionData.status, 10)]
            : undefined,
      stageId: Array.isArray(actionData.stageId)
        ? actionData.stageId
        : actionData.stageId
          ? [actionData.stageId]
          : undefined,
      subStageId: Array.isArray(actionData.subStageId)
        ? actionData.subStageId
        : actionData.subStageId
          ? [actionData.subStageId]
          : undefined,
      scoreRange: (() => {
        const val = actionData.leadScore || actionData.scoreRange;
        if (!val) return undefined;
        // Handle array format and convert "-" to "|" for SP compatibility
        const actualVal = Array.isArray(val) ? val[0] : val;
        return typeof actualVal === "string" ? actualVal.replace("-", "|") : undefined;
      })(),
      assignedTo: Array.isArray(actionData.assignedTo)
        ? actionData.assignedTo
        : actionData.assignedTo
          ? [actionData.assignedTo]
          : undefined,
      teamId: actionData.teamId || undefined,
      state: actionData.state || undefined,
      fromDate: actionData.fromDate
        ? new Date(actionData.fromDate).toISOString()
        : undefined,
      toDate: actionData.toDate
        ? new Date(actionData.toDate).toISOString()
        : undefined,
      ...((() => {
        const tab = actionData?.applicantTab;
        if (tab === "all")        return { isApplication: true,  isStudent: true  };
        if (tab === "applicants") return { isApplication: true,  isStudent: false };
        if (tab === "students")   return { isApplication: false, isStudent: true  };
        return { 
          isApplication: actionData?.IsApplication !== undefined ? actionData.IsApplication : actionData.isApplication,
          isStudent: actionData?.IsStudent !== undefined ? actionData.IsStudent : actionData.isStudent
        };
      })()),
      includeAllFiltered: actionData.includeAllFiltered !== undefined ? actionData.includeAllFiltered : undefined,
      leadIds: Array.isArray(actionData.leadIds) ? actionData.leadIds : undefined,
      staticFields: Array.isArray(actionData.staticFields) && actionData.staticFields.length > 0 ? actionData.staticFields : undefined,
      loggedInUserId: actionData.loggedInUserId || getUserIdFromToken(),
      viewMode: actionData.viewMode !== undefined ? actionData.viewMode : undefined,
      pageNumber: actionData.pageNumber > 0 ? actionData.pageNumber : 1, // Use 1-based indexing to match backend expectations
      pageSize: actionData.pageSize || 25,
      advancedFilters: Array.isArray(actionData.advancedFilters) && actionData.advancedFilters.length > 0 ? actionData.advancedFilters : undefined,
      groupedFilters: actionData.groupedFilters || undefined,
      companyId: actionData.companyId || getCompanyIdFromToken(),
      sortColumn: actionData.sortColumn || (actionData.sortBy ? (SORT_COLUMN_MAPPING[actionData.sortBy] || actionData.sortBy) : undefined),
      sortDirection: actionData.sortDirection || actionData.sortOrder || undefined,
      leadview: actionData.leadview !== undefined ? actionData.leadview : 0,

      // Tag filter — structured Include/Exclude list (same as getLeads)
      tags: Array.isArray(actionData.tagNames) && actionData.tagNames.length > 0
        ? actionData.tagNames
        : undefined,


      actionType: actionData.actionType !== undefined ? actionData.actionType : undefined,
      updateStageId: actionData.updateStageId || undefined,
      updateSubStageId: actionData.updateSubStageId || undefined,
      updateAssignedTo: actionData.updateAssignedTo || undefined,
      remarks: actionData.remarks || undefined,
    };

    // Remove undefined keys
    Object.keys(payload).forEach((key) => {
      if (payload[key] === undefined) {
        delete payload[key];
      }
    });

    // Make API call using POST
    const response = await axiosInstance.post(API_ENDPOINTS.BULK_ACTION, payload);
    const responseData = response.data;

    // Parse the response based on the API structure
    if (responseData.statusCode === 200 && responseData.status === "Success") {
      // Parse updatedCount from data field if it exists and is a string
      let updatedCount = 0;
      // console.log("API Response Data:", responseData.data); 

      if (responseData.data) {
        if (typeof responseData.data === 'string') {
          try {
            const parsedData = JSON.parse(responseData.data);
            updatedCount = parsedData.updatedCount || parsedData.length || 0;
            // console.log("Parsed updatedCount from string:", updatedCount);
          } catch (parseError) {
            console.error("Error parsing updatedCount from data field:", parseError);
            updatedCount = 0;
          }
        } else if (Array.isArray(responseData.data)) {
          // If data is an array, count the elements
          updatedCount = responseData.data.length;
          // console.log("Got updatedCount from array length:", updatedCount); 
        } else if (typeof responseData.data === 'object') {
          updatedCount = responseData.data.updatedCount || responseData.data.length || 0;
          // console.log("Got updatedCount from object:", updatedCount); 
        }
      }

      return {
        success: true,
        message: responseData.message || "Bulk action completed successfully",
        data: responseData.data,
        updatedCount: updatedCount,
      };
    } else {
      throw new Error(
        responseData.message || "Failed to execute bulk action",
      );
    }
  } catch (error) {
    console.error("Error executing bulk action:", error);
    return {
      success: false,
      error: error.message || "Failed to execute bulk action",
      data: null,
    };
  }
};

// Cache for stages and statuses
let stagesCache = null;
let statusesCache = null;

// Get stage name by ID
export const getStageNameById = async (stageId) => {
  try {
    if (!stagesCache) {
      const stages = await getStageAndSubStages({ pageSize: 100 });
      stagesCache = stages;
    }

    const stage = stagesCache.find((s) => s.stageId === stageId);
    return stage?.stageName || `Stage ${stageId}`;
  } catch (error) {
    console.error("Error getting stage name:", error);
    return `Stage ${stageId}`;
  }
};

// Get substage name by stage ID and substage ID
export const getSubStageNameById = async (stageId, subStageId) => {
  try {
    if (!stagesCache) {
      const stages = await getStageAndSubStages({ pageSize: 100 });
      stagesCache = stages;
    }

    const stage = stagesCache.find((s) => s.stageId === stageId);
    const subStage = stage?.subStages?.find(
      (ss) => ss.subStageId === subStageId,
    );
    return subStage?.subStageName || `SubStage ${subStageId}`;
  } catch (error) {
    console.error("Error getting substage name:", error);
    return `SubStage ${subStageId}`;
  }
};

// Get status name by ID
export const getStatusNameById = (statusId) => {
  const statusMap = {
    0: "New",
    1: "Contacted",
    2: "Qualified",
    3: "Converted",
    4: "Lost",
    5: "Follow Up",
    6: "Not Interested",
  };

  return statusMap[statusId] || `Status ${statusId}`;
};

// Get all stages for dropdown/reference
export const getAllStages = async () => {
  try {
    if (!stagesCache) {
      const stages = await getStageAndSubStages({ pageSize: 100 });
      stagesCache = stages;
    }
    return stagesCache;
  } catch (error) {
    console.error("Error getting all stages:", error);
    return [];
  }
};

// Clear cache (useful for refreshing data)
export const clearStagesCache = () => {
  stagesCache = null;
};

// ===== TIMELINE OPERATIONS =====

// Get lead timeline activities with pagination
export const getLeadTimeline = async (
  leadId,
  pageNumber = 1,
  pageSize = 10,
) => {
  try {
    const payload = {
      leadId,
      pageNumber,
      pageSize,
    };

    const response = await axiosInstance.post(
      `${API_ENDPOINTS.LEADS}/GetTimelineByLeadId`,
      payload,
    );
    const responseData = response.data;

    if (responseData.statusCode === 200 && responseData.status === "Success") {
      // Parse the data if it's a JSON string
      let timelineData = [];
      if (typeof responseData.data === "string") {
        try {
          const parsedData = JSON.parse(responseData.data);
          timelineData = Array.isArray(parsedData) ? parsedData : [parsedData];
        } catch (parseError) {
          console.error("Error parsing timeline data:", parseError);
          timelineData = [];
        }
      } else if (Array.isArray(responseData.data)) {
        timelineData = responseData.data;
      } else if (responseData.data) {
        timelineData = [responseData.data];
      }

      // Extract activities from the timeline data structure
      let activities = [];
      if (timelineData.length > 0 && timelineData[0].Activities) {
        activities = timelineData[0].Activities;
      } else if (timelineData.length > 0) {
        activities = timelineData;
      }

      return {
        success: true,
        data: activities,
        totalRecords: responseData.totalRecords || activities.length,
        message: responseData.message || "Timeline fetched successfully",
      };
    } else {
      throw new Error(responseData.message || "Failed to fetch timeline");
    }
  } catch (error) {
    console.error("Error fetching lead timeline:", error);
    return {
      success: false,
      error: error.message || "Failed to fetch timeline",
      data: [],
      totalRecords: 0,
    };
  }
};

// Export leads data
export const exportLeads = async (filters = {}, format = "csv") => {
  try {
    const response = await axiosInstance.post(`${API_ENDPOINTS.LEADS}/export`, {
      filters,
      format,
    });
    const responseData = response.data;

    if (responseData.statusCode === 200 && responseData.status === "Success") {
      return {
        success: true,
        data: responseData.data,
        message: responseData.message || "Export generated successfully",
      };
    } else {
      throw new Error(responseData.message || "Failed to export leads");
    }
  } catch (error) {
    console.error("Error exporting leads:", error);
    return {
      success: false,
      error: error.message || "Failed to export leads",
      data: null,
    };
  }
};

// Export leads using bulk action dispatcher structure
export const exportLeadsWithBulkStructure = async (exportData) => {
  try {
    const {
      // Filter parameters (same as bulkLeadActionDispatcher)
      search,
      leadSourceId,
      leadStatus,
      stageId,
      subStageId,
      scoreRange,
      assignedTo,
      teamId,
      state,
      fromDate,
      toDate,
      isApplication,
      includeAllFiltered,
      leadIds,
      staticFields,
      loggedInUserId,
      viewMode,
      pageNumber,
      pageSize,
      advancedFilters,
      groupedFilters,
      companyId,
      sortColumn,
      sortDirection,
      leadview,
      utmSources,
      timeZoneId,

      // Export specific parameters
      selectedFieldIds,
    } = exportData;

    // Build the payload following the bulk action dispatcher structure
    const payload = {
      // Filter parameters
      search: exportData.search || undefined,
      leadSourceId: Array.isArray(exportData.leadSourceId)
        ? exportData.leadSourceId
        : exportData.leadSourceId && exportData.leadSourceId !== "all"
          ? [exportData.leadSourceId]
          : exportData.source && exportData.source !== "all"
            ? [exportData.source]
            : undefined,
      leadStatus: Array.isArray(exportData.leadStatus)
        ? exportData.leadStatus
        : exportData.leadStatus && exportData.leadStatus !== "all"
          ? [parseInt(exportData.leadStatus, 10)]
          : exportData.status && exportData.status !== "all"
            ? [parseInt(exportData.status, 10)]
            : undefined,
      stageId: Array.isArray(exportData.stageId)
        ? exportData.stageId
        : exportData.stageId
          ? [exportData.stageId]
          : undefined,
      subStageId: Array.isArray(exportData.subStageId)
        ? exportData.subStageId
        : exportData.subStageId
          ? [exportData.subStageId]
          : undefined,
      scoreRange: (() => {
        const val = exportData.leadScore || exportData.scoreRange;
        if (!val) return undefined;
        // Handle array format and convert "-" to "|" for SP compatibility
        const actualVal = Array.isArray(val) ? val[0] : val;
        return typeof actualVal === "string" ? actualVal.replace("-", "|") : undefined;
      })(),
      assignedTo: Array.isArray(exportData.assignedTo)
        ? exportData.assignedTo
        : exportData.assignedTo
          ? [exportData.assignedTo]
          : undefined,
      teamId: exportData.teamId || undefined,
      state: exportData.state || undefined,
      fromDate: exportData.fromDate
        ? new Date(exportData.fromDate).toISOString()
        : undefined,
      toDate: exportData.toDate
        ? new Date(exportData.toDate).toISOString()
        : undefined,
      ...((() => {
        const tab = exportData?.applicantTab;
        if (tab === "all")        return { isApplication: true,  isStudent: true  };
        if (tab === "applicants") return { isApplication: true,  isStudent: false };
        if (tab === "students")   return { isApplication: false, isStudent: true  };
        return { 
          isApplication: exportData?.IsApplication !== undefined ? exportData.IsApplication : exportData.isApplication,
          isStudent: exportData?.IsStudent !== undefined ? exportData.IsStudent : exportData.isStudent
        };
      })()),
      includeAllFiltered: exportData.includeAllFiltered !== undefined ? exportData.includeAllFiltered : undefined,
      leadIds: Array.isArray(exportData.leadIds) ? exportData.leadIds : undefined,
      staticFields: Array.isArray(exportData.staticFields) && exportData.staticFields.length > 0 ? exportData.staticFields : undefined,
      loggedInUserId: exportData.loggedInUserId || getUserIdFromToken(),
      viewMode: exportData.viewMode !== undefined ? exportData.viewMode : undefined,
      pageNumber: exportData.pageNumber > 0 ? exportData.pageNumber : 1, // Use 1-based indexing to match backend expectations
      pageSize: exportData.pageSize || 25,
      advancedFilters: Array.isArray(exportData.advancedFilters) && exportData.advancedFilters.length > 0 ? exportData.advancedFilters : undefined,
      groupedFilters: exportData.groupedFilters || undefined,
      companyId: exportData.companyId || getCompanyIdFromToken(),
      sortColumn: exportData.sortColumn || (exportData.sortBy ? (SORT_COLUMN_MAPPING[exportData.sortBy] || exportData.sortBy) : undefined),
      sortDirection: exportData.sortDirection || exportData.sortOrder || undefined,
      leadview: exportData.leadview !== undefined ? exportData.leadview : 0,
      utmSources: exportData.utmSources || undefined,
      timeZoneId: timeZoneId || undefined,

      // Export specific parameters
      selectedFieldIds: Array.isArray(exportData.selectedFieldIds) ? exportData.selectedFieldIds : undefined,
    };

    // Remove undefined keys
    Object.keys(payload).forEach((key) => {
      if (payload[key] === undefined) {
        delete payload[key];
      }
    });

    // Make API call using POST for export
    const response = await axiosInstance.post("/api/Leads/export/leads", payload, { responseType: "blob" });

    return {
      success: true,
      data: response.data, // Return blob data for file download
      message: "Export generated successfully",
    };
  } catch (error) {
    console.error("Error exporting leads:", error);
    return {
      success: false,
      error: error.message || "Failed to export leads",
      data: null,
    };
  }
};


export const getExportableStaticFields = async () => {
  try {
    const response = await axiosInstance.get(
      `${API_ENDPOINTS.LEADS}/GetExportableStaticFields`
    );

    const responseData = response.data;

    if (responseData.statusCode === 200 && responseData.status === "Success") {
      let fields = [];

      // Parse data if it's a JSON string
      if (typeof responseData.data === "string") {
        try {
          const parsedData = JSON.parse(responseData.data);
          fields = Array.isArray(parsedData) ? parsedData : [parsedData];
        } catch (parseError) {
          console.error("Error parsing exportable static fields:", parseError);
          fields = [];
        }
      } else if (Array.isArray(responseData.data)) {
        fields = responseData.data;
      } else if (responseData.data) {
        fields = [responseData.data];
      }

      return {
        success: true,
        data: fields,
        totalRecords: responseData.totalRecords || fields.length,
        message:
          responseData.message ||
          "Exportable static fields fetched successfully",
      };
    } else {
      throw new Error(
        responseData.message || "Failed to fetch exportable static fields"
      );
    }
  } catch (error) {
    console.error("Error fetching exportable static fields:", error);

    return {
      success: false,
      error:
        error.message || "Failed to fetch exportable static fields",
      data: [],
      totalRecords: 0,
    };
  }
};
// ===== LEAD DOCUMENTS =====

// Get lead documents with filtering and pagination
export const getLeadDocuments = async (params = {}) => {
  try {
    const response = await axiosInstance.post(
      `${API_ENDPOINTS.LEADS}/GetLeadDocuments`,
      {
        leadId: params.leadId,
        fileType: params.fileType || null,
        search: params.search || null,
        startDate: params.startDate || null,
        endDate: params.endDate || null,
        pageNumber: params.pageNumber || 1,
        pageSize: params.pageSize || 10,
      },
    );

    const responseData = response.data;

    if (responseData.statusCode === 200 && responseData.status === "Success") {
      let documentsData = [];

      if (typeof responseData.data === "string") {
        try {
          documentsData = JSON.parse(responseData.data);
        } catch (parseError) {
          console.error("Error parsing documents data:", parseError);
          documentsData = [];
        }
      } else if (Array.isArray(responseData.data)) {
        documentsData = responseData.data;
      }

      return {
        success: true,
        data: documentsData,
        totalRecords: responseData.totalRecords || documentsData.length,
        message: responseData.message || "Documents fetched successfully",
      };
    } else {
      throw new Error(responseData.message || "Failed to fetch documents");
    }
  } catch (error) {
    console.error("Error fetching lead documents:", error);
    return {
      success: false,
      error: error.message || "Failed to fetch documents",
      data: [],
      totalRecords: 0,
    };
  }
};

// Get document verification history
export const getDocumentVerificationHistory = async (params = {}) => {
  try {
    const response = await axiosInstance.post(
      `${API_ENDPOINTS.LEADS}/GetDocumentVerificationHistory`,
      {
        leadId: params.leadId,
        fileType: params.fileType || null,
        search: params.search || null,
        startDate: params.startDate || null,
        endDate: params.endDate || null,
        pageNumber: params.pageNumber || 1,
        pageSize: params.pageSize || 10,
      },
    );

    const responseData = response.data;

    if (responseData.statusCode === 200 && responseData.status === "Success") {
      let documentsData = [];

      if (typeof responseData.data === "string") {
        try {
          documentsData = JSON.parse(responseData.data);
        } catch (parseError) {
          console.error("Error parsing documents data:", parseError);
          documentsData = [];
        }
      } else if (Array.isArray(responseData.data)) {
        documentsData = responseData.data;
      }

      return {
        success: true,
        data: documentsData,
        totalRecords: responseData.totalRecords || documentsData.length,
        message: responseData.message || "Document verification history fetched successfully",
      };
    } else {
      throw new Error(responseData.message || "Failed to fetch document verification history");
    }
  } catch (error) {
    console.error("Error fetching document verification history:", error);
    return {
      success: false,
      error: error.message || "Failed to fetch document verification history",
      data: [],
      totalRecords: 0,
    };
  }
};

// Upload lead documents
export const uploadLeadDocuments = async (leadId, files) => {
  try {
    const formData = new FormData();

    // Add leadId to form data
    formData.append("leadId", leadId);
    // console.log("Adding leadId:", leadId);

    // Add each file to form data
    files.forEach((file, index) => {
      formData.append("files", file);
      // console.log(`Adding file ${index}:`, file.name, file.size, file.type);
    });

    // Log FormData contents for debugging
    // console.log("FormData contents:");
    for (let [key, value] of formData.entries()) {
      // console.log(`${key}:`, value);
    }

    const response = await axiosInstance.post(
      `${API_ENDPOINTS.LEADS}/UploadLeadDocuments`,
      formData,
      {
        // Don't set Content-Type here - let the browser set it with correct boundary
        // The axios interceptor automatically handles FormData Content-Type
        timeout: 120000, // 2 minute timeout for larger file uploads
      },
    );

    const responseData = response.data;

    if (responseData.statusCode === 200 && responseData.status === "Success") {
      return {
        success: true,
        data: responseData.data,
        message: responseData.message || "Documents uploaded successfully",
      };
    } else {
      // For validation errors, return the complete response instead of throwing
      return {
        success: false,
        message: responseData.message || "Upload failed",
        status: responseData.status,
        data: responseData.data,
        statusCode: responseData.statusCode,
        totalRecords: responseData.totalRecords,
      };
    }
  } catch (error) {
    console.error("Error uploading lead documents:", error);
    // console.log("Error object structure:", error);

    // Check if the error object directly contains the server response structure
    if (error.status && error.data) {
      const responseData = error;
      // console.log("Using direct error object as response:", responseData);
      return {
        success: false,
        message: responseData.message || "Upload failed",
        status: responseData.status,
        data: responseData.data,
        statusCode: responseData.statusCode,
        totalRecords: responseData.totalRecords,
      };
    }

    // Fallback for other errors
    // console.log("Using generic error handling");
    return {
      success: false,
      message: error.message || "Failed to upload documents",
      data: null,
    };
  }
};

// Delete lead document
export const deleteLeadDocument = async (documentId) => {
  try {
    const response = await axiosInstance.delete(
      `${API_ENDPOINTS.LEADS}/DeleteleadDocument?documentId=${documentId}`,
    );
    const responseData = response.data;

    if (responseData.statusCode === 200 && responseData.status === "Success") {
      return {
        success: true,
        message: responseData.message || "Document deleted successfully",
      };
    } else {
      throw new Error(responseData.message || "Failed to delete document");
    }
  } catch (error) {
    console.error("Error deleting lead document:", error);
    return {
      success: false,
      error: error.message || "Failed to delete document",
    };
  }
};

/**
 * Send email to a lead
 * @param {string} leadId - Lead ID
 * @param {string} email - Lead email address
 * @param {string} subject - Email subject
 * @param {string} body - Email body content
 * @param {Array<string>} cc - CC email addresses (optional)
 * @param {Array<string>} bcc - BCC email addresses (optional)
 * @param {Array<File|string>} attachments - File objects or URLs (optional)
 * @param {string} ProviderCode - Email service provider code (optional)
 * @param {string|null} templateId - Template ID if using a template (optional)
 * @returns {Promise<Object>} - Response from API
 */
export const sendEmailToLead = async (leadId, email, subject, body, cc = [], bcc = [], attachments = [], ProviderCode = "", templateId = null) => {
  try {
    // Use FormData for multipart/form-data
    const formData = new FormData();

    // Add leads as string (comma-separated IDs or JSON)
    formData.append('leads', leadId);
    formData.append('Subject', subject);
    formData.append('Body', body);
    formData.append('ProviderCode', ProviderCode);

    // Add template ID if provided
    if (templateId) {
      formData.append('TemplateId', templateId);
    }

    // Add CC and BCC as comma-separated strings
    if (cc && cc.length > 0) {
      formData.append('CC', cc.join(','));
    }
    if (bcc && bcc.length > 0) {
      formData.append('Bcc', bcc.join(','));
    }

    // Add attachments - handle both File objects and template attachments
    if (attachments && attachments.length > 0) {
      for (const file of attachments) {
        if (file instanceof File) {
          // New file uploads from user's computer
          formData.append('Attachments', file);
        } else if (file.templateAttachment && file.path) {
          // Template attachment - need to fetch it as a blob first
          continue;
        } else if (typeof file === 'string') {
          // If it's a URL string - fetch and convert to File
          try {
            const response = await fetch(file);
            const blob = await response.blob();
            const fileName = file.split('/').pop() || 'attachment';
            const fileObject = new File([blob], fileName, { type: blob.type });
            formData.append('Attachments', fileObject);
          } catch (fetchError) {
            console.error('Error fetching attachment from URL:', fetchError);
            toast.error('Failed to load some attachments');
          }
        }
      }
    }

    const response = await axiosInstance.post(
      `${API_ENDPOINTS.EMAIL}/send-to-lead`,
      formData,
      {
        // Content-Type handled by axios interceptor for FormData
        timeout: 60000, // 1 minute timeout for email with attachments
      }
    );

    if (
      response.data.statusCode === 200 &&
      (response.data.status === "Success" || response.data.status === "success")
    ) {
      return {
        success: true,
        data: response.data.data,
        message: response.data.message || "Email sent successfully",
      };
    } else {
      throw new Error(response.data.message || "Failed to send email");
    }
  } catch (error) {
    console.error("Error sending email:", error);

    let errorMessage = "Failed to send email";
    if (error.response) {
      const status = error.response.status;
      const serverMessage =
        error.response.data?.message || error.response.data?.error;
      if (status >= 400 && status < 500) {
        errorMessage = serverMessage || `Client error: ${status}`;
      } else if (status >= 500) {
        errorMessage = serverMessage || `Server error: ${status}`;
      }
    } else if (error.request) {
      errorMessage = "Network error: Unable to connect to server";
    } else {
      errorMessage = error.message || "Unknown error occurred";
    }

    throw new Error(errorMessage);
  }
};


/**
 * Send email to multiple leads
 * @param {Array} leadsData - Array of lead objects with id and email
 * @param {string} subject - Email subject
 * @param {string} body - Email body content
 * @param {Array<string>} cc - CC email addresses (optional)
 * @param {Array<string>} bcc - BCC email addresses (optional)
 * @param {Array<File|string>} attachments - File objects or URLs (optional)
 * @param {string} ProviderCode - Email service provider code (optional)
 * @param {string|null} templateId - Template ID if using a template (optional)
 * @returns {Promise<Object>} - Response from API
 */
export const sendEmailToMultipleLeads = async (leadsData, subject, body, cc = [], bcc = [], attachments = [], ProviderCode = "", templateId = null) => {
  try {
    // Use FormData for multipart/form-data
    const formData = new FormData();

    // Build leads as comma-separated IDs
    const leadIds = leadsData.map(lead => lead.id).filter(Boolean).join(',');

    formData.append('leads', leadIds);
    formData.append('Subject', subject);
    formData.append('Body', body);
    formData.append('ProviderCode', ProviderCode);

    // Add template ID if provided
    if (templateId) {
      formData.append('TemplateId', templateId);
    }

    // Add CC and BCC as comma-separated strings
    if (cc && cc.length > 0) {
      formData.append('CC', cc.join(','));
    }
    if (bcc && bcc.length > 0) {
      formData.append('Bcc', bcc.join(','));
    }

    // Add attachments - handle both File objects and template attachments
    if (attachments && attachments.length > 0) {
      for (const file of attachments) {
        if (file instanceof File) {
          formData.append('Attachments', file);
        } else if (file.templateAttachment && file.path) {
          try {
            const response = await fetch(file.path);
            const blob = await response.blob();
            const fileObject = new File([blob], file.name, { type: blob.type });
            formData.append('Attachments', fileObject);
          } catch (fetchError) {
            console.error('Error fetching template attachment:', fetchError);
          }
        } else if (typeof file === 'string') {
          try {
            const response = await fetch(file);
            const blob = await response.blob();
            const fileName = file.split('/').pop() || 'attachment';
            const fileObject = new File([blob], fileName, { type: blob.type });
            formData.append('Attachments', fileObject);
          } catch (fetchError) {
            console.error('Error fetching attachment from URL:', fetchError);
          }
        }
      }
    }
    const response = await axiosInstance.post(
      `${API_ENDPOINTS.EMAIL}/send-to-lead`,
      formData,
      {
        // Content-Type handled by axios interceptor for FormData
        timeout: 60000, // 1 minute timeout for email with attachments
      }
    );

    if (
      response.data.statusCode === 200 &&
      (response.data.status === "Success" || response.data.status === "success")
    ) {
      return {
        success: true,
        data: response.data.data,
        message:
          response.data.message ||
          `Email sent to ${Object.keys(leads).length} leads successfully`,
      };
    } else {
      throw new Error(response.data.message || "Failed to send emails");
    }
  } catch (error) {
    console.error("Error sending bulk email:", error);

    let errorMessage = "Failed to send emails";
    if (error.response) {
      const status = error.response.status;
      const serverMessage =
        error.response.data?.message || error.response.data?.error;
      if (status >= 400 && status < 500) {
        errorMessage = serverMessage || `Client error: ${status}`;
      } else if (status >= 500) {
        errorMessage = serverMessage || `Server error: ${status}`;
      }
    } else if (error.request) {
      errorMessage = "Network error: Unable to connect to server";
    } else {
      errorMessage = error.message || "Unknown error occurred";
    }

    throw new Error(errorMessage);
  }
};

/**
 * Send SMS to a lead
 * @param {string} leadId - Lead ID
 * @param {string} mobile - Lead mobile number
 * @param {string} message - SMS message content
 * @returns {Promise<Object>} - Response from API
 */
export const sendSmsToLead = async (leadId, mobile, message, providerCode = null) => {
  try {
    const requestBody = {
      leads: {
        [leadId]: mobile,
      },
      message,
      ...(providerCode && { providerCode }),
    };


    const response = await axiosInstance.post(
      `${API_ENDPOINTS.SMS}/send-to-lead`,
      requestBody,
    );

    if (
      response.data.statusCode === 200 &&
      (response.data.status === "Success" || response.data.status === "success")
    ) {
      return {
        success: true,
        data: response.data.data,
        message: response.data.message || "SMS sent successfully",
      };
    } else {
      throw new Error(response.data.message || "Failed to send SMS");
    }
  } catch (error) {
    console.error("Error sending SMS:", error);

    let errorMessage = "Failed to send SMS";
    if (error.response) {
      const status = error.response.status;
      const serverMessage =
        error.response.data?.message || error.response.data?.error;
      if (status >= 400 && status < 500) {
        errorMessage = serverMessage || `Client error: ${status}`;
      } else if (status >= 500) {
        errorMessage = serverMessage || `Server error: ${status}`;
      }
    } else if (error.request) {
      errorMessage = "Network error: Unable to connect to server";
    } else {
      errorMessage = error.message || "Unknown error occurred";
    }

    throw new Error(errorMessage);
  }
};

/**
 * Send SMS to multiple leads
 * @param {Array} leadsData - Array of lead objects with id and mobile
 * @param {string} message - SMS message content
 * @returns {Promise<Object>} - Response from API
 */
export const sendSmsToMultipleLeads = async (leadsData, message, providerCode = null) => {
  try {
    // Build leads object with leadId: mobile format
    // Backend will fetch mobile numbers from DB if null/missing
    const leads = {};
    leadsData.forEach((lead) => {
      if (lead.id) {
        // Include all leads with ID, even if mobile is null
        // Backend will fetch the mobile number from database
        leads[lead.id] = lead.mobile || null;
      }
    });

    const requestBody = {
      leads,
      message,
      ...(providerCode && { providerCode }),
    };


    const response = await axiosInstance.post(
      `${API_ENDPOINTS.SMS}/send-to-lead`,
      requestBody,
    );

    if (
      response.data.statusCode === 200 &&
      (response.data.status === "Success" || response.data.status === "success")
    ) {
      return {
        success: true,
        data: response.data.data,
        message:
          response.data.message ||
          `SMS sent to ${Object.keys(leads).length} leads successfully`,
      };
    } else {
      throw new Error(response.data.message || "Failed to send SMS messages");
    }
  } catch (error) {
    console.error("Error sending bulk SMS:", error);

    let errorMessage = "Failed to send SMS messages";
    if (error.response) {
      const status = error.response.status;
      const serverMessage =
        error.response.data?.message || error.response.data?.error;
      if (status >= 400 && status < 500) {
        errorMessage = serverMessage || `Client error: ${status}`;
      } else if (status >= 500) {
        errorMessage = serverMessage || `Server error: ${status}`;
      }
    } else if (error.request) {
      errorMessage = "Network error: Unable to connect to server";
    } else {
      errorMessage = error.message || "Unknown error occurred";
    }

    throw new Error(errorMessage);
  }
};

/**
 * Get communication timeline for a lead
 * @param {string} leadId - Lead ID
 * @param {number} channelId - Channel ID (1: EMAIL, 2: SMS, 3: WHATSAPP, 0: ALL)
 * @param {number} pageNumber - Page number for pagination
 * @param {number} pageSize - Number of records per page
 * @returns {Promise<Object>} - Response from API
 */
export const getCommunicationTimeline = async (
  leadId,
  channelId = 0,
  pageNumber = 1,
  pageSize = 10,
) => {
  try {
    const payload = {
      leadId,
      channelId,
      pageNumber,
      pageSize,
    };

    const response = await axiosInstance.post(
      `${API_ENDPOINTS.LEADS}/GetCommunicationTimeLine`,
      payload,
    );
    const responseData = response.data;

    if (responseData.statusCode === 200 && responseData.status === "Success") {
      // Parse the data if it's a JSON string
      let timelineData = [];
      if (typeof responseData.data === "string") {
        try {
          timelineData = JSON.parse(responseData.data);
        } catch (parseError) {
          console.error(
            "Error parsing communication timeline data:",
            parseError,
          );
          timelineData = [];
        }
      } else if (Array.isArray(responseData.data)) {
        timelineData = responseData.data;
      }

      return {
        success: true,
        data: timelineData,
        totalRecords: responseData.totalRecords || timelineData.length,
        message:
          responseData.message || "Communication timeline fetched successfully",
      };
    } else {
      throw new Error(
        responseData.message || "Failed to fetch communication timeline",
      );
    }
  } catch (error) {
    console.error("Error fetching communication timeline:", error);
    return {
      success: false,
      error: error.message || "Failed to fetch communication timeline",
      data: [],
      totalRecords: 0,
    };
  }
};

/**
 * Get communication and telephony status for a lead
 * @param {string} leadId - Lead ID
 * @returns {Promise<Object>} - Response from API with communication and telephony status
 */
export const getCommunicationAndTelephonyStatus = async (leadId) => {
  try {
    const response = await axiosInstance.get(
      `${API_ENDPOINTS.LEADS}/GetCommunicationAndTelephonyStatus?leadId=${leadId}`,
    );
    const responseData = response.data;

    if (responseData.statusCode === 200 && responseData.status === "Success") {
      // Parse the data if it's a JSON string
      let statusData = {};
      if (typeof responseData.data === "string") {
        try {
          statusData = JSON.parse(responseData.data);
        } catch (parseError) {
          console.error(
            "Error parsing communication and telephony status data:",
            parseError,
          );
          statusData = {};
        }
      } else if (
        typeof responseData.data === "object" &&
        responseData.data !== null
      ) {
        statusData = responseData.data;
      }

      return {
        success: true,
        data: statusData,
        totalRecords: responseData.totalRecords || 1,
        message:
          responseData.message ||
          "Communication and telephony status fetched successfully",
      };
    } else {
      throw new Error(
        responseData.message ||
        "Failed to fetch communication and telephony status",
      );
    }
  } catch (error) {
    console.error("Error fetching communication and telephony status:", error);
    return {
      success: false,
      error:
        error.message || "Failed to fetch communication and telephony status",
      data: {},
      totalRecords: 0,
    };
  }
};

/**
 * Create a user account for a lead
 * @param {string} leadId - The lead ID
 * @returns {Promise<{success: boolean, message: string, data: any, totalRecords: number}>}
 */
export const createLeadUser = async (leadId) => {
  try {
    if (!leadId) {
      return {
        success: false,
        message: "Lead ID is required",
        data: null,
        totalRecords: 0,
      };
    }

    const companyId = getCompanyIdFromToken();
    if (!companyId) {
      return {
        success: false,
        message: "Company ID not found in token",
        data: null,
        totalRecords: 0,
      };
    }

    const payload = {
      companyId,
      leadId,
    };

    const response = await axiosInstance.post(
      "/api/Leads/CreateLeadUser",
      payload
    );

    const responseData = response.data;

    if (responseData.statusCode === 200 && responseData.status === "Success") {
      return {
        success: true,
        message: responseData.message || "User created successfully",
        data: responseData.data,
        totalRecords: responseData.totalRecords || 1,
      };
    } else {
      throw new Error(
        responseData.message || "Failed to create lead user"
      );
    }
  } catch (error) {
    console.error("Error creating lead user:", error);
    return {
      success: false,
      message: error.message || "Failed to create lead user",
      data: null,
      totalRecords: 0,
    };
  }
};

// Get lead duplicates
export const getLeadDuplicates = async (leadId) => {
  try {
    if (!leadId) {
      return {
        success: false,
        message: "Lead ID is required",
        data: [],
        totalRecords: 0,
      };
    }

    const response = await axiosInstance.get(
      "/api/Leads/GetLeadDuplicates",
      {
        params: {
          LeadId: leadId,
        },
      }
    );

    const responseData = response.data;

    if (responseData.statusCode === 200 && responseData.status === "Success") {
      // Parse the data if it's a JSON string
      let parsedData = responseData.data;
      if (typeof parsedData === 'string') {
        try {
          parsedData = JSON.parse(parsedData);
        } catch (e) {
          parsedData = [];
        }
      }

      return {
        success: true,
        message: responseData.message || "Lead duplicates fetched successfully",
        data: parsedData || [],
        totalRecords: responseData.totalRecords || 0,
      };
    } else {
      throw new Error(
        responseData.message || "Failed to fetch lead duplicates"
      );
    }
  } catch (error) {
    console.error("Error fetching lead duplicates:", error);
    return {
      success: false,
      message: error.message || "Failed to fetch lead duplicates",
      data: [],
      totalRecords: 0,
    };
  }
};

/**
 * Get users assigned to document verification
 * @param {string} documentId - The document ID
 * @returns {Promise<Object>} - Response with assigned users and their verification status
 */
export const getUsersAssignedToDocVerification = async (documentId) => {
  try {
    if (!documentId) {
      throw new Error("Document ID is required");
    }

    const response = await axiosInstance.get(
      `/api/Leads/GetUserAssignedToDocVerification/${documentId}`
    );

    const responseData = response.data;

    if (responseData.statusCode === 200 && responseData.status === "Success") {
      // Parse the data if it's a JSON string
      let parsedData = responseData.data;
      if (typeof parsedData === 'string') {
        try {
          parsedData = JSON.parse(parsedData);
        } catch (e) {
          parsedData = [];
        }
      }

      return {
        success: true,
        message: responseData.message || "Assigned users fetched successfully",
        data: parsedData || [],
        totalRecords: responseData.totalRecords || 0,
      };
    } else {
      throw new Error(
        responseData.message || "Failed to fetch assigned users"
      );
    }
  } catch (error) {
    console.error("Error fetching assigned users:", error);
    return {
      success: false,
      message: error.message || "Failed to fetch assigned users",
      data: [],
      totalRecords: 0,
    };
  }
};

/**
 * Get leads assigned for document verification
 * @param {Object} params - Query parameters
 * @param {string} params.search - Search term
 * @param {number} params.pageNumber - Page number for pagination
 * @param {number} params.pageSize - Number of records per page

 * @returns {Promise<Object>} - Response with assigned leads data
 */
export const getLeadAssignedForDocVerification = async ({
  search = "",
  pageNumber = 1,
  pageSize = 10,
  isPending = false,
  isApprovedView = false
} = {}) => {
  try {
    // Derive company and user from JWT like other methods
    const companyId = getCompanyIdFromToken() || getCompanyId(); // matches existing pattern [7]
    const modifiedBy = getUserIdFromToken();
    const payload = {
      search,
      pageNumber,
      pageSize,
      loggedInUserId: modifiedBy,
      companyId,
      isPending,
      isApprovedView
    };

    const response = await axiosInstance.post(
      "/api/Leads/GetLeadAsignedForDocVerification",
      payload, { showLoader: false }
    );

    const responseData = response.data;

    if (responseData.statusCode === 200 && responseData.status === "Success") {
      // Parse the data if it's a JSON string
      let leadsData = [];
      if (typeof responseData.data === "string") {
        try {
          leadsData = JSON.parse(responseData.data);
        } catch (parseError) {
          console.error("Error parsing leads data:", parseError);
          leadsData = [];
        }
      } else if (Array.isArray(responseData.data)) {
        leadsData = responseData.data;
      }

      return {
        success: true,
        data: leadsData,
        totalRecords: responseData.totalRecords || leadsData.length,
        message: responseData.message || "Assigned leads fetched successfully",
      };
    } else {
      throw new Error(responseData.message || "Failed to fetch assigned leads");
    }
  } catch (error) {
    console.error("Error fetching assigned leads:", error);
    return {
      success: false,
      error: error.message || "Failed to fetch assigned leads",
      data: [],
      totalRecords: 0,
    };
  }
};

export const getDocumentVerificationCounts = async () => {
  try {
    const response = await axiosInstance.get("/api/Leads/GetDocumentVerificationCounts");
    const responseData = response.data;

    if (responseData.statusCode === 200 && responseData.status === "Success") {
      let parsedData = {};
      if (typeof responseData.data === "string") {
        try {
          parsedData = JSON.parse(responseData.data);
        } catch (err) {
          console.error("Error parsing DocumentVerificationCounts data:", err);
          parsedData = {};
        }
      } else {
        parsedData = responseData.data || {};
      }
      return {
        success: true,
        data: parsedData,
        message: responseData.message ?? "Document verification counts fetched successfully",
      };
    }
    throw new Error(responseData.message || "Failed to fetch document verification counts");
  } catch (error) {
    console.error("API Error:", error);

    return {
      success: false,
      data: {},
      message: error.message || "Failed to fetch document verification counts",
    };
  }
};

export const updateLeadDetail = async (payload) => {
  try {
    const response = await axiosInstance.post('/api/Leads/UpdateLeadDetail', payload);

    return {
      success: response.data?.statusCode === 200,
      data: response.data?.data
        ? JSON.parse(response.data.data)
        : null,
      message: response.data?.message || 'Lead updated successfully'
    };

  } catch (error) {
    console.error('Error updating lead:', error);

    return {
      success: false,
      message: error.response?.data?.message || 'Failed to update lead',
      error: true
    };
  }
};

export const counselorVerifyPhoneEmail = async (payload) => {
  try {
    const response = await axiosInstance.post(
      '/api/Account/CounselorVerifyPhoneEmail',
      payload
    );

    return {
      success: response.data?.statusCode === 200,
      data: response.data?.data
        ? JSON.parse(response.data.data)
        : null,
      message: response.data?.message || 'Verification completed successfully'
    };

  } catch (error) {
    console.error('Error verifying phone/email:', error);

    return {
      success: false,
      message: error.response?.data?.message || 'Failed to verify phone/email',
      error: true
    };
  }
};


export const sendResendPhoneEmailOtpByUser = async (payload) => {
  try {
    const response = await axiosInstance.post(
      '/api/Account/SendResendPhoneEmailOtpByUser',
      payload
    );

    return {
      success: response.data?.statusCode === 200,
      data: response.data?.data
        ? JSON.parse(response.data.data)   // because data is returned as a string
        : null,
      message: response.data?.message || 'OTP sent successfully',
    };

  } catch (error) {
    console.error('Error sending/resending OTP:', error);

    return {
      success: false,
      message:
        error.response?.data?.message ||
        'Failed to send/resend OTP',
      error: true,
    };
  }
};

export const verifyPhoneEmailOtpByUser = async (payload) => {
  try {
    const response = await axiosInstance.post(
      '/api/Account/VerifyPhoneEmailOtpByUser',
      payload
    );

    return {
      success: response.data?.statusCode === 200,
      data: response.data?.data
        ? JSON.parse(response.data.data) // backend sends data as a string
        : null,
      message: response.data?.message || 'Verification successful',
    };

  } catch (error) {
    console.error('Error verifying OTP:', error);

    return {
      success: false,
      message:
        error.response?.data?.message ||
        'Failed to verify OTP',
      error: true,
    };
  }
};

// Export constants for easy access
export {
  LEAD_STATUSES,
  LEAD_STAGES,
  LEAD_SOURCES,
  LEAD_PRIORITIES,
  LEAD_OWNERS,
  PROGRAMS,
  COURSES,
  INDIAN_STATES,
};
